import React, { useContext, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import {
    PoliticalExposureInformation,
    PoliticalExposurePoliticalAffiliateInformation
} from "../../model/PoliticalExposureType";
import { ZahtjevContext } from "../../common/context/ZahtjevProvider";
import { CreateZahtjevPolitickaIzlozenostRequest, useUpdatePoliticalExposure } from "../../api/RegistrationApi";
import { useNotification } from "../../common/context/NotificationProvider";
import { Formik, FormikErrors, FormikValues } from "formik";
import { Button } from "../common/Button";
import Switch from "../common/Switch";
import InputField from "../common/InputField";
import { validateField, validateForm } from "./politicalExposureInformationValidation";
import { ValidateOnMount } from "../common/ValidateOnMount";
import { Card } from "../common/Card";
import { ERROR_MESSAGE_NOTIFICATION } from "../../common/constants/notificationConstants";
import { POLITICAL_EXPOSURE_INFORMATION_STATUS } from "../../common/constants/navigationConstants";
import { toNullOrString } from "../../util/stringUtil";
import { PoliticalExposurePoliticalAffiliateInput } from "./PoliticalExposurePoliticalAffiliateInput";
import AddIcon from "@mui/icons-material/Add";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import { scrollToFirstError } from "../../util/formUtil";

const POLITICKE_OSOBE_MAX_LENGTH = 3;

const INITIAL_POLITICAL_EXPOSURE_INFORMATION_FORMIK_VALUES: PoliticalExposureInformation = {
    diplomat: false,
    sudRevizorski: false,
    sudVrhovniUstavni: false,
    clanUpravnogTijelaPolitika: false,
    clanZakonodavnogTijela: false,
    celnikDrzava: false,
    celnikLokalnaVlast: false,
    clanBracni: false,
    clanMedjunarodnaOrganizacija: false,
    clanNadzornogOdbora: false,
    clanPotomak: false,
    clanRoditelj: false,
    clanSavjetaSredisnjeBanke: false,
    suradnikPoslovni: false,
    suradnikPoslovniZajedno: false,
    izvorSredstava: '',
    izvorSredstavaBanka: '',
    politickeOsobe: [],
};

const EMPTY_POLITICKA_OSOBA: PoliticalExposurePoliticalAffiliateInformation = {
    imePrezime: "",
    funkcija: "",
};

const checkForPolitickeOsobe = (values: PoliticalExposureInformation) => {
    return values.clanBracni || values.clanPotomak || values.clanRoditelj || values.suradnikPoslovniZajedno || values.suradnikPoslovni;
}

const withAtLeastOnePolitickaOsoba = (values: PoliticalExposureInformation): PoliticalExposureInformation => ({
    ...values,
    politickeOsobe: checkForPolitickeOsobe(values) && !values.politickeOsobe?.length
      ? [EMPTY_POLITICKA_OSOBA]
      : values.politickeOsobe ?? [],
});

function mapPolticalExposureInformationToCreateZahtjevPolitickaIzlozenostRequest(input: PoliticalExposureInformation): CreateZahtjevPolitickaIzlozenostRequest {
    return {
        celnikDrzava: input.celnikDrzava ?? false,
        clanZakonodavnogTijela: input.clanZakonodavnogTijela ?? false,
        clanUpravnogTijelaPolitika: input.clanUpravnogTijelaPolitika ?? false,
        sudVrhovniUstavni: input.sudVrhovniUstavni ?? false,
        sudRevizorski: input.sudRevizorski ?? false,
        clanSavjetaSredisnjeBanke: input.clanSavjetaSredisnjeBanke ?? false,
        diplomat: input.diplomat ?? false,
        clanNadzornogOdbora: input.clanNadzornogOdbora ?? false,
        clanMedjunarodnaOrganizacija: input.clanMedjunarodnaOrganizacija ?? false,
        celnikLokalnaVlast: input.celnikLokalnaVlast ?? false,
        clanBracni: input.clanBracni ?? false,
        clanPotomak: input.clanPotomak ?? false,
        clanRoditelj: input.clanRoditelj ?? false,
        suradnikPoslovniZajedno: input.suradnikPoslovniZajedno ?? false,
        suradnikPoslovni: input.suradnikPoslovni ?? false,
        izvorSredstava: toNullOrString(input.izvorSredstava),
        izvorSredstavaBanka: toNullOrString(input.izvorSredstavaBanka),
        politickeOsobe: input.politickeOsobe,
    }
}

const PoliticalExposurePage = () => {
    const { t } = useTranslation();
    const {
        politicalExposure,
        politicalExposureCallback,
        getZahtjevId,
        restoreContext,
        getStatus
    } = useContext(ZahtjevContext);
    const {
        isPending: isLoading,
        mutate: updatePoliticalExposureMutation
    } = useUpdatePoliticalExposure(getZahtjevId());
    const { notify } = useNotification();
    const currentStatus = getStatus();
    const pageStatus = POLITICAL_EXPOSURE_INFORMATION_STATUS;
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const initalPoliticalExposureInformationFormValues = withAtLeastOnePolitickaOsoba(
      (Object.keys(politicalExposure).length === 0)
        ? INITIAL_POLITICAL_EXPOSURE_INFORMATION_FORMIK_VALUES
        : politicalExposure
    );
    const navigate = useNavigate();

    const handleSubmit = (values: PoliticalExposureInformation, setFieldError: (field: string, message: (string | undefined)) => void) => {
        const isFormValid = validateForm(values, setFieldError);

        if (isFormValid) {
            const request = mapPolticalExposureInformationToCreateZahtjevPolitickaIzlozenostRequest(values);

            updatePoliticalExposureMutation(request, {
                onSuccess: () => {
                    politicalExposureCallback(values);
                    navigate("/documentation-delivery");
                },
                onError: () => {
                    notify(ERROR_MESSAGE_NOTIFICATION, "error");
                }
            });
        } else {
            scrollToFirstError();
        }
    };

    const handleReturn = (values: PoliticalExposureInformation, dirty: boolean) => {
        if (dirty) politicalExposureCallback(values);
        navigate('/tax-residency', { state: { isBack: true } });
    };

    const checkForIzvoriSredstava = (values: PoliticalExposureInformation) => {
        return values.celnikDrzava || values.clanZakonodavnogTijela || values.clanUpravnogTijelaPolitika || values.sudVrhovniUstavni || values.sudRevizorski
          || values.sudRevizorski || values.clanSavjetaSredisnjeBanke || values.diplomat || values.clanNadzornogOdbora || values.clanMedjunarodnaOrganizacija || values.celnikLokalnaVlast
          || values.clanBracni || values.clanPotomak || values.clanRoditelj || values.suradnikPoslovniZajedno || values.suradnikPoslovni;
    }

    const handleValidation = (setFieldError: (field: string, message: (string | undefined)) => void) =>
      (value: any, field?: string) => {
          return validateField(field!, value, setFieldError)
      };

    const handleSetFieldValue = (
      values: PoliticalExposureInformation,
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
      setFieldError: (field: string, message: (string | undefined)) => void = () => {
      }
    ) =>
      (value: any, field?: string) => {
          const currentCheckForIzvoriSredstava = checkForIzvoriSredstava(values);
          const newCheckForIzvoriSredstava = checkForIzvoriSredstava({ ...values, [field!]: value });

          if (newCheckForIzvoriSredstava !== currentCheckForIzvoriSredstava && !newCheckForIzvoriSredstava) {
              setFieldValue("izvorSredstava", "", false);
              setFieldError("izvorSredstava", undefined);
              setFieldValue("izvorSredstavaBanka", "", false);
              setFieldError("izvorSredstavaBanka", undefined);
          }

          if (field === "clanBracni" || field === "clanPotomak" || field === "clanRoditelj" || field === "suradnikPoslovniZajedno" || field === "suradnikPoslovni") {
              const currentPolitickeOsobeState = checkForPolitickeOsobe(values);
              const newPolitickeOsobeState = checkForPolitickeOsobe({ ...values, [field]: value });
              if (currentPolitickeOsobeState && !newPolitickeOsobeState) {
                  setFieldValue("politickeOsobe", [], false);
                  setFieldError("politickeOsobe", undefined);
              } else if (!currentPolitickeOsobeState && newPolitickeOsobeState) {
                  setFieldValue("politickeOsobe", [EMPTY_POLITICKA_OSOBA], false);
              }
          }

          return setFieldValue(field!, value, false);
      };

    const handleAddPolitickaOsoba = (politickeOsobe: PoliticalExposurePoliticalAffiliateInformation[], setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>) => {
        politickeOsobe.push(EMPTY_POLITICKA_OSOBA);
        setFieldValue(`politickeOsobe`, politickeOsobe, false);
    };

    const handleRemovePolitickaOsoba = (
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
      setFieldError: (field: string, message: string | undefined) => void,
      politickeOsobe: PoliticalExposurePoliticalAffiliateInformation[],
    ) => (index: number) => {
        setFieldValue("politickeOsobe", politickeOsobe.filter((_, i) => i !== index), false);
        setFieldError(`politickeOsobe[${index}]`, undefined);
    }

    useEffect(() => {
        restoreContext(pageStatus);
    }, []);

    return (
      <Card title={t("political-exposure-page.title")} isLoading={parseInt(currentStatus) < parseInt(pageStatus)}
            bodyClassName="md:w-full">
          <Formik
            initialValues={initalPoliticalExposureInformationFormValues}
            onSubmit={(values, { setFieldError }) => handleSubmit(values, setFieldError)}
          >{({ values, errors, handleSubmit, setFieldError, setFieldValue, dirty }) => (
            <form noValidate onSubmit={handleSubmit} autoComplete="off">
                <ValidateOnMount shouldValidate={Object.keys(politicalExposure).length !== 0} validate={validateForm} />
                <div className="space-y-5 flex flex-col">
                    <p>
                        {t("political-exposure-page.description")}
                    </p>
                    <Switch
                      name="celnikDrzava"
                      label={t("political-exposure-page.state-head-field.label")}
                      defaultValue={values.celnikDrzava}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="clanZakonodavnogTijela"
                      label={t("political-exposure-page.legislature-member-field.label")}
                      defaultValue={values.clanZakonodavnogTijela}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="clanUpravnogTijelaPolitika"
                      label={t("political-exposure-page.governing-body-member-field.label")}
                      defaultValue={values.clanUpravnogTijelaPolitika}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="sudVrhovniUstavni"
                      label={t("political-exposure-page.constitutional-or-supreme-court-member-field.label")}
                      defaultValue={values.sudVrhovniUstavni}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="sudRevizorski"
                      label={t("political-exposure-page.auditors-court-member-field.label")}
                      defaultValue={values.sudRevizorski}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="clanSavjetaSredisnjeBanke"
                      label={t("political-exposure-page.central-bank-council-member-field.label")}
                      defaultValue={values.clanSavjetaSredisnjeBanke}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="diplomat"
                      label={t("political-exposure-page.diplomat-field.label")}
                      defaultValue={values.diplomat}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="clanNadzornogOdbora"
                      label={t("political-exposure-page.supervisory-board-member-field.label")}
                      defaultValue={values.clanNadzornogOdbora}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="clanMedjunarodnaOrganizacija"
                      label={t("political-exposure-page.international-organizations-member-field.label")}
                      defaultValue={values.clanMedjunarodnaOrganizacija}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="celnikLokalnaVlast"
                      label={t("political-exposure-page.local-government-chief-field.label")}
                      defaultValue={values.celnikLokalnaVlast}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <p className="text-md">
                        {t("political-exposure-page.political-affiliate.direct-relation.info")}
                    </p>

                    <Switch
                      name="clanBracni"
                      label={t("political-exposure-page.political-affiliate.direct-relation.married-contact")}
                      defaultValue={values.clanBracni}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="clanPotomak"
                      label={t("political-exposure-page.political-affiliate.direct-relation.descendant-contact")}
                      defaultValue={values.clanPotomak}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="clanRoditelj"
                      label={t("political-exposure-page.political-affiliate.direct-relation.parent-contact")}
                      defaultValue={values.clanRoditelj}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <p className="text-md">
                        {t("political-exposure-page.political-affiliate.side-relation.info")}
                    </p>

                    <Switch
                      name="suradnikPoslovniZajedno"
                      label={t("political-exposure-page.political-affiliate.side-relation.business-co-owner-contact")}
                      defaultValue={values.suradnikPoslovniZajedno}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    <Switch
                      name="suradnikPoslovni"
                      label={t("political-exposure-page.political-affiliate.side-relation.business-contact")}
                      defaultValue={values.suradnikPoslovni}
                      handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                    />

                    {(values.clanBracni || values.clanPotomak || values.clanRoditelj || values.suradnikPoslovniZajedno || values.suradnikPoslovni) && (
                      <>
                          <p className="text-md">
                              {t("political-exposure-page.political-affiliate.side-relation.contacts-list.info")}
                          </p>
                          {values.politickeOsobe?.map((politickaOsoba, index) => {
                              return (
                                <PoliticalExposurePoliticalAffiliateInput
                                  key={index}
                                  index={index}
                                  value={politickaOsoba}
                                  error={errors.politickeOsobe ? (errors.politickeOsobe[index] as FormikErrors<PoliticalExposurePoliticalAffiliateInformation>) : undefined}
                                  handleValueChange={handleSetFieldValue(values, setFieldValue, setFieldError)}
                                  handleValidation={handleValidation(setFieldError)}
                                  handleRemoval={handleRemovePolitickaOsoba(setFieldValue, setFieldError, values.politickeOsobe)}
                                  isRemovalEnabled={values?.politickeOsobe?.length > 1}
                                />
                              )
                          })
                          }
                          {(values?.politickeOsobe?.length < POLITICKE_OSOBE_MAX_LENGTH) && (
                            <Button
                              className={isMobile ? "w-full" : "w-fit"}
                              onClick={() => handleAddPolitickaOsoba(values.politickeOsobe, setFieldValue)}
                              type="button"
                              text={t("political-exposure-page.political-affiliate.side-relation.contacts-list.button-add-person")}
                              img={<AddIcon fontSize="medium" />}
                            />
                          )}
                      </>
                    )}

                    {checkForIzvoriSredstava(values) &&
                      <div className="space-y-5 flex flex-col">
                        <p className="text-md">
                            {t("political-exposure-page.political-affiliate.funds-instruction")}
                        </p>

                        <InputField
                          name="izvorSredstava"
                          label={t("political-exposure-page.political-affiliate.fund-sources-field.label")}
                          maxLength={1000}
                          multiline={true}
                          defaultValue={values?.izvorSredstava || ""}
                          error={t(errors.izvorSredstava as string)}
                          handleValueChange={handleSetFieldValue(values, setFieldValue)}
                          handleValidation={handleValidation(setFieldError)}
                        />

                        <InputField
                          name="izvorSredstavaBanka"
                          label={t("political-exposure-page.political-affiliate.fund-bank-sources-field.label")}
                          maxLength={1000}
                          multiline={true}
                          defaultValue={values?.izvorSredstavaBanka || ""}
                          error={t(errors.izvorSredstavaBanka as string)}
                          handleValueChange={handleSetFieldValue(values, setFieldValue)}
                          handleValidation={handleValidation(setFieldError)}
                        />
                      </div>
                    }
                </div>

                <div className="mt-10 text-center md:text-left">
                    <Button
                      type="button"
                      onClick={() => handleReturn(values, dirty)}
                      text={t("common.button-previous")}
                      variant="secondary"
                      disabled={false}
                    />
                    <Button
                      type="submit"
                      text={t("common.button-next")}
                      disabled={isLoading}
                    />
                </div>
            </form>
          )}
          </Formik>
      </Card>
    );
};

export default PoliticalExposurePage;
